import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader, EmptyState } from "@/components/Common";
import { GraduationCap, Search, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CELL = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  due: "bg-amber-50 text-amber-700 border-amber-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
  completed: "bg-sky-50 text-sky-700 border-sky-200",
  none: "bg-slate-50 text-slate-300 border-slate-100",
};

const STATUS_LABEL = {
  all: "All employees",
  overdue: "Only overdue",
  due: "Overdue or due ≤ 30d",
  ok: "On track",
};

const label = (c) => {
  if (c.cell === "none") return "—";
  if (c.cell === "completed") return "Complete";
  if (c.cell === "overdue") return `Overdue ${Math.abs(c.days)}d`;
  return `${c.days}d left`;
};

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-xs text-slate-500 mb-4">
    {[["ok", "On track"], ["due", "Due ≤ 30d"], ["overdue", "Overdue"], ["completed", "Complete"], ["none", "Not assigned"]].map(([k, t]) => (
      <span key={k} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded border ${CELL[k]}`} /> {t}</span>
    ))}
  </div>
);

const rowMatchesStatus = (row, filter) => {
  if (filter === "all") return true;
  const cells = Object.values(row.cells || {});
  if (filter === "overdue") return cells.some((c) => c.cell === "overdue");
  if (filter === "due") return cells.some((c) => c.cell === "overdue" || c.cell === "due");
  if (filter === "ok") return cells.every((c) => c.cell === "ok" || c.cell === "completed" || c.cell === "none");
  return true;
};

export default function TrainingMatrix() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => { api.get("/training-matrix").then((r) => setData(r.data)).catch(() => {}); }, []);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (!rowMatchesStatus(r, statusFilter)) return false;
      if (!needle) return true;
      return [r.employee, r.title, r.employee_role].some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [data, q, statusFilter]);

  const sendMatrixReminders = async () => {
    setSending(true);
    try {
      const res = await api.post("/reminders/matrix-send-now");
      const { sent = 0, missing_email = [] } = res.data || {};
      if (sent === 0 && missing_email.length === 0) toast.success("No overdue or due-soon items. Everyone is on track!");
      else toast.success(`Sent ${sent} reminder email${sent === 1 ? "" : "s"}${missing_email.length ? ` · ${missing_email.length} missing email (CC'd to pharmacy)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send reminders"); }
    finally { setSending(false); }
  };

  return (
    <div>
      <PageHeader
        testid="training-header"
        title="Training & Competency Matrix"
        subtitle="Every employee's media fills, competencies, and license status at a glance."
      >
        {isAdmin && (
          <Button data-testid="send-matrix-reminders-btn" onClick={sendMatrixReminders} disabled={sending} className="bg-pink-700 hover:bg-pink-800 gap-1.5 h-9">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send Matrix Reminders
          </Button>
        )}
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="matrix-search" placeholder="Search by name, title or role…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" />
        </div>
        <select data-testid="matrix-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <Legend />
      <Card className="p-0 overflow-hidden border-slate-200">
        {(!data || filteredRows.length === 0) ? (
          <EmptyState icon={GraduationCap} title={q || statusFilter !== "all" ? "No employees match your filter" : "No employees to display"}
            hint={q || statusFilter !== "all" ? "Try clearing the search or status filter." : "Add employees and assign compliance tasks to populate the matrix."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="training-matrix-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left p-3 text-xs font-semibold text-slate-600 sticky left-0 bg-slate-50 z-10 min-w-[200px]">Employee</th>
                  {data.categories.map((c) => (
                    <th key={c} className="text-left p-3 text-xs font-semibold text-slate-600 min-w-[130px]">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.employee} data-testid={`training-row-${row.employee}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="p-3 sticky left-0 bg-white z-10">
                      <div className="font-medium text-slate-800 text-sm">{row.employee}</div>
                      <div className="text-xs text-slate-400">{row.title || row.employee_role}</div>
                    </td>
                    {data.categories.map((cat) => {
                      const c = row.cells[cat] || { cell: "none" };
                      return (
                        <td key={cat} className="p-3">
                          <div className={`inline-flex flex-col items-start px-2.5 py-1.5 rounded-lg border text-xs font-medium ${CELL[c.cell]}`}>
                            <span>{label(c)}</span>
                            {c.due_date && c.cell !== "none" && <span className="font-mono text-[10px] opacity-70 mt-0.5">{c.due_date}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
